import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ShieldCheck } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { type SsoConnection, ssoListConnectionsOptions, ssoListConnectionsQueryKey } from '@column/api';
import { Button } from '@column/ui';

import { RenderFields } from '~/components';
import { getErrorMessage } from '~/lib/api/getErrorMessage';

import { ConnectionCard } from './ConnectionCard';
import { ConnectionDialog } from './ConnectionDialog';
import { FEATURE_NOT_ENABLED } from './constants';
import { useCanManageSSO } from './useCanManageSSO';
import { getErrorCode } from './utils';

interface SSOSectionFields {
  connection?: never;
}

export const SSOConnection: React.FC = () => {
  const canManage = useCanManageSSO();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ key: number; connectionId?: string }>();
  const formMethods = useForm<SSOSectionFields>();

  const connectionsQuery = useQuery({
    ...ssoListConnectionsOptions(),
    enabled: canManage,
    retry: (failureCount, error) => getErrorCode(error) !== FEATURE_NOT_ENABLED && failureCount < 3,
  });
  const connections = connectionsQuery.data?.sso_connections ?? [];

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ssoListConnectionsQueryKey() }),
    [queryClient]
  );

  const openDialog = (connection?: SsoConnection) => setDialog({ key: Date.now(), connectionId: connection?.id });

  if (!canManage || connectionsQuery.isPending || getErrorCode(connectionsQuery.error) === FEATURE_NOT_ENABLED) {
    return null;
  }

  return (
    <>
      <FormProvider {...formMethods}>
        {RenderFields<SSOSectionFields>({
          sections: [
            {
              headline: 'Single Sign-On',
              description: "Allow your team to sign in to the dashboard with your company's identity provider.",
              fields: [
                {
                  id: 'connection',
                  label: 'Connection',
                  children: () =>
                    connectionsQuery.isError ? (
                      <p className="text-sm text-muted-foreground">
                        {getErrorMessage(connectionsQuery.error, 'Unable to load SSO connection.')}
                      </p>
                    ) : connections.length > 0 ? (
                      <div className="grid gap-3">
                        {connections.map((connection) => (
                          <ConnectionCard
                            key={connection.id}
                            connection={connection}
                            onEdit={() => openDialog(connection)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="@container rounded-lg bg-gray-25 px-4 py-3 ring-1 ring-black/5 dark:bg-white/[0.02] dark:ring-white/10">
                        <div className="flex items-center gap-3 @max-[420px]:flex-col @max-[420px]:items-stretch">
                          <div className="flex min-w-0 flex-auto items-center gap-3">
                            <ShieldCheck className="size-6 self-start text-gray-400 dark:text-gray-500" />
                            <div className="flex min-w-0 flex-auto flex-col gap-0.5">
                              <span className="text-sm font-medium text-foreground">Add connection</span>
                              <span className="text-xs text-muted-foreground">Connect an OIDC identity provider.</span>
                            </div>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="@max-[420px]:w-full"
                            onClick={() => openDialog()}
                          >
                            <Plus />
                            Add
                          </Button>
                        </div>
                      </div>
                    ),
                },
              ],
            },
          ],
        })}
      </FormProvider>

      {dialog && (
        <ConnectionDialog
          key={dialog.key}
          connection={connections.find((connection) => connection.id === dialog.connectionId)}
          onClose={() => setDialog(undefined)}
          onSaved={invalidate}
          onCreated={(connectionId) => setDialog((current) => current && { ...current, connectionId })}
        />
      )}
    </>
  );
};
